import { useState } from "react";
import * as Yup from "yup";
import { COMPANY_FIRST_NAME, REFERRAL_IMG } from "@/_constant";
import { Button, SideDrawer, TextInput } from "@/_component/Common";
import InputLabel from "@/_component/Common/form/fields/LabelInput";
import { addOrUpdateReferralDiscount } from "@/apis";
import { Error, Success } from "@/_service/notification/notification";
import { constructValidationError, copyToClipboard, renderError, timeout, validateSchema } from "@/utility";

interface Props {
  isOpen: boolean;
  referralLink: string;
  onClose: () => void;
  onShare: () => void;
}

interface ReferralForm {
  userId: string;
  discountAmount: number | string;
  isAgree: boolean;
}

const referralSchema = Yup.object().shape({
  userId: Yup.string().required("User mobile no is required"),
  discountAmount: Yup.number()
    .typeError("Discount Amount must be valid number")
    .min(0, "Min discount amount can be 0")
    .max(19, "Max discount amount can be 19")
    .required("Discount amount is required"),
  isAgree: Yup.boolean()
    .oneOf([true], "Agree terms and condition is required")
    .required("Agree terms and condition is required"),
});

const ReferralDrawer = ({ isOpen, referralLink, onClose, onShare }: Props) => {
  const [form, setForm] = useState<ReferralForm>({ userId: "", discountAmount: 0, isAgree: false });
  const [errors, setErrors] = useState<Partial<Record<keyof ReferralForm, string>> | null>(null);
  const [isCustomDiscount, setIsCustomDiscount] = useState(false);

  const updateDiscount = (value: number | "CUSTOM") => {
    setIsCustomDiscount(value === "CUSTOM");
    setForm((current) => ({ ...current, discountAmount: value === "CUSTOM" ? "" : value }));
    setErrors((current) => current ? ({ ...current, discountAmount: undefined }) : null);
  };

  const validate = () => {
    try {
      validateSchema(referralSchema, form);
      setErrors(null);
      return true;
    } catch (error) {
      setErrors(constructValidationError(error) as Partial<Record<keyof ReferralForm, string>>);
      timeout(() => setErrors(null), 5000);
      return false;
    }
  };

  const save = async () => {
    if (!validate()) return false;
    try {
      await addOrUpdateReferralDiscount({ userId: form.userId.trim(), discountAmount: Number(form.discountAmount) });
      Success("Discount coupon linked successfully");
      return true;
    } catch (error: any) {
      Error(`Failed to link discount coupon. Reason: ${error?.data?.response?.message || "Unknown"}`);
      return false;
    }
  };

  const copyLink = async () => {
    if (!(await save())) return;
    await copyToClipboard(referralLink);
    Success("Link copied to clipboard");
  };

  const shareLink = async () => {
    if (await save()) {
      onClose && onClose();
      onShare();
    }
  };

  return (
    <SideDrawer isOpen={isOpen} onClose={onClose} title="Referral & Incentive" side="right" closeOnOutsideClick showCloseButton noPadding>
      <div className="position-relative d-flex flex-column h-100">
        <div className="overflow-y-auto">
          <div className="app-bg-secondary-light text-center">
            <img src={REFERRAL_IMG} className="img-fluid" alt="Referral" />
          </div>
          <div className="px-3 mt-4">
            <p className="text-dark text-bold">Choose Referral Incentive</p>
            <div className="d-flex flex-wrap gap-2">
              {[0, 5, 10, 15].map((discount) => (
                <Button key={discount} small primary outline={isCustomDiscount || Number(form.discountAmount) !== discount} solid={!isCustomDiscount && Number(form.discountAmount) === discount} onClick={() => updateDiscount(discount)}>
                  {discount === 0 ? "No Discount" : `${discount}%`}
                </Button>
              ))}
              <Button small primary outline={!isCustomDiscount} solid={isCustomDiscount} onClick={() => updateDiscount("CUSTOM")}>Custom</Button>
            </div>

            {isCustomDiscount && (
              <div className="mt-3">
                <InputLabel label="Discount amount" required />
                <TextInput value={form.discountAmount} name="discountAmount" placeholder="16" onChange={(_, __, value) => setForm((current) => ({ ...current, discountAmount: String(value ?? "") }))} />
                {renderError(errors, "discountAmount")}
              </div>
            )}

            <div className="mt-3">
              <InputLabel label="New user mobile number" required />
              <TextInput value={form.userId} name="userId" placeholder="98700XXXXX" onChange={(_, __, value) => setForm((current) => ({ ...current, userId: String(value ?? "") }))} />
              {renderError(errors, "userId")}
            </div>

            <div className="kg-bg-mustard d-flex gap-2 align-items-center p-2 mt-3 text-white rounded-2">
              <i className="bi bi-sunrise-fill" />
              <small>The discount you offer will be deducted from your commission. {COMPANY_FIRST_NAME} does not sponsor this discount.</small>
            </div>
          </div>
        </div>

        <div className="mt-auto w-100 py-3 px-3 border-top">
          <div className="form-check mb-3">
            <input className="form-check-input" type="checkbox" id="agree-referral-terms" checked={form.isAgree} onChange={(event) => setForm((current) => ({ ...current, isAgree: event.target.checked }))} />
            <label htmlFor="agree-referral-terms" className="form-check-label fs-7">I have read, accepted and understand the commission calculations</label>
            {renderError(errors, "isAgree")}
          </div>
          <div className="d-flex gap-2">
            <Button primary outline className="w-100" onClick={copyLink}>Copy Link</Button>
            <Button primary solid className="w-100" onClick={shareLink}>Share Link</Button>
          </div>
        </div>
      </div>
    </SideDrawer>
  );
};

export default ReferralDrawer;
